import React from 'react'
import { Modal, Form, Col, Row, Button } from 'react-bootstrap'
import { createModal, ModalResolveType, useModalAction } from '@src/lib/modal-manager'
import { useFetch } from '@src/hooks'
import { useForm, Controller } from '@src/lib/react-hook-form'
import z from 'zod'
import { CarService } from '@src/domain/services'
import { CarModelDto } from '@src/domain/repositories/setting.repo'

interface EditCarModelProps {
  dtoUuid?: string;
  itemDto?: CarModelDto;
}

const initState: CarModelDto = {
  carBrandUuid: '',
  code: '',
  modelYear: '',
  isQRCodeRequired: false,
  status: '1'
}

export function EditCarModelModalWrapper(props: EditCarModelProps) {
  const { resolve, remove, hide } = useModalAction()
  const { dtoUuid, itemDto } = props
  
  const yearRegex = /^\d{4}$/

  const { register, handleSubmit, formState: { errors }, control } = useForm<CarModelDto>({
    reValidateMode: 'onSubmit',
    defaultValues: {...initState, ...itemDto},
    rules: z.object({
      carBrandUuid: z.string().min(1, { message: '請選擇品牌' }),
      code: z.string().min(1, { message: '請輸入車種' }),
      modelYear: z.string().refine(v => yearRegex.test(v), { message: '請輸入西元年' }),
      isQRCodeRequired: z.boolean(),
      status: z.string().min(1, {message: '請選擇狀態'})
    })
  })

  const { data: carBrandOptions = [] } = useFetch(CarService.getCarBrands, null)

  const handleClose = () => {
    resolve(ModalResolveType.CLOSED)
    hide()
    remove()
  }

  const handleCancel = () => {
    resolve(ModalResolveType.CANCEL)
    hide()
    remove()
  }

  const handleOnSubmit = (formDto: CarModelDto) => {
    resolve(ModalResolveType.OK, formDto)
    hide()
    remove()
  }

  return (
    <Modal size='lg' show onHide={handleClose} centered>
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>{`${dtoUuid ? '編輯' : '新增'}車種`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              品牌
            </Form.Label>
            <Col sm='10'>
              <Controller
                name='carBrandUuid'
                control={control}
                render={({ field }) => <Form.Select {...field}>
                  <option value=''>請選擇品牌</option>
                  {carBrandOptions.map(option => <option key={option.uuid} value={option.uuid}>{option.code}</option>)}
                </Form.Select>}
              />
            </Col>
            {errors.carBrandUuid && <Form.Text className='text-danger'>{errors.carBrandUuid.message}</Form.Text>}
          </Form.Group>

          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              車種
            </Form.Label>
            <Col sm='10'>
              <Form.Control placeholder='Ex: ALPHARD HV' { ...register('code') } />
            </Col>
            {errors.code && <Form.Text className='text-danger'>{errors.code.message}</Form.Text>}
          </Form.Group>

          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              年式
            </Form.Label>
            <Col sm='10'>
              <Form.Control placeholder='Ex: 2025' { ...register('modelYear') } />
            </Col>
            {errors.modelYear && <Form.Text className='text-danger'>{errors.modelYear.message}</Form.Text>}
          </Form.Group>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2' />
            <Col sm='10'>
              <Form.Check
                type='checkbox'
                label='需記錄車身QRCode(大車)'
                { ...register('isQRCodeRequired') }
              />
            </Col>
          </Form.Group>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              狀態
            </Form.Label>
            <Col sm='10'>
              <Form.Select {...register('status')}>
                <option value='1'>啟用</option>
                <option value='0'>停用</option>
              </Form.Select>
            </Col>
            {errors.status && <Form.Text className='text-danger'>{errors.status.message}</Form.Text>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCancel} variant='light'>
            取消
          </Button>
          <Button type='submit'>
            確認
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const EditCarModelModal = createModal(EditCarModelModalWrapper)
